import React, { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { makeStyles } from 'tss-react/mui';
import { ContentCollectionType, HeroModuleItemType } from '../../../../../API';
import { FormMetadata, useData } from '../../../../../data-layer';
import { useLocales, useStatsig } from '../../../../../hooks';
import Button from '../../../../shared/Button';
import Drawer from '../../../../shared/Drawer';
import { markAsRequired } from '../../../../../utils/formHelpers';
import { contentItemFormTestIds } from '../../../../shared/TestsIds';
import type { ContentItem } from '../ContentItemPreview';
import { ContentTypeSelector } from '../../../../Layouts/UIModuleForm/ContentTypeSelector';
import ContentPicker from '../../../../Layouts/UIModuleForm/ContentPicker';
import { useSetRecoilState } from 'recoil';
import { StatsigConfigs } from '../../../../../utils/consts/statsigGates';

const useStyles = makeStyles()((theme) => ({
  formBody: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4)
  },
  footerButton: {
    minWidth: 120
  },
  formRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  errorLabel: {
    color: theme.palette.error.main
  },
  contentPicker: {
    flexGrow: 1,
    gridColumn: 'span 2'
  }
}));

export interface ContentItemFormMetadata extends FormMetadata<ContentItem> {
  index?: number;
}

export type ContentItemFormProps = {
  formMetadata: ContentItemFormMetadata | undefined;
  onSubmit: (formMetadata: ContentItemFormMetadata) => void;
  onDelete: (formMetadata: ContentItemFormMetadata) => void;
};

// Map DataPresetType to display labels for content types
const contentTypeOptions = {
  [HeroModuleItemType.VOD]: HeroModuleItemType.VOD
};

export function ContentItemForm({ formMetadata, onSubmit, onDelete }: ContentItemFormProps): React.ReactElement {
  const formRef = useRef<ValidatorForm>(null);
  const { classes, cx } = useStyles();
  const { t } = useLocales();
  const { GetConfig } = useStatsig();
  const formMethods = useForm<ContentItem>();
  const { handleSubmit, reset, control, watch } = formMethods;
  const [isDrawerShowing, setIsDrawerShowing] = useState(false);
  const itemType = watch('type');

  const {
    media: {
      state: { withContentTypesAllowed }
    }
  } = useData();

  const campaignsConfig = GetConfig(StatsigConfigs.Campaigns);
  const setContentTypesAllowed = useSetRecoilState(withContentTypesAllowed);

  const getContentCollectionTypesAllowed = () => {
    const excludedTypes = campaignsConfig?.value?.excludedContentTypes as ContentCollectionType[] | undefined;
    if (!excludedTypes || !Array.isArray(excludedTypes)) {
      return Object.values(ContentCollectionType);
    }
    return Object.values(ContentCollectionType).filter((type) => !excludedTypes.includes(type));
  };

  useEffect(() => {
    if (!formMetadata?.record) return;
    reset(formMetadata.record);

    if (campaignsConfig) {
      setContentTypesAllowed(getContentCollectionTypesAllowed());
    }

    setIsDrawerShowing(true);
  }, [formMetadata, reset, campaignsConfig]);

  const renderFormLabel = (label: string, hasError?: boolean) => (
    <Typography
      component="div"
      color="textSecondary"
      variant="body2"
      className={cx({ [classes.errorLabel]: hasError })}
    >
      {label}
    </Typography>
  );

  const handleClose = () => {
    setIsDrawerShowing(false);
  };

  const handleDeleteClick = async () => {
    if (!formMetadata) return;
    onDelete(formMetadata);
    handleClose();
  };

  const onFormSubmit: SubmitHandler<ContentItem> = async (submittedContentItem) => {
    if (!formMetadata) return;
    onSubmit({
      ...formMetadata,
      record: submittedContentItem
    });
    handleClose();
  };

  const onSaveClick = () => {
    (formRef.current as ValidatorForm & { submit: () => void }).submit();
  };

  return (
    <Drawer
      open={isDrawerShowing}
      formRef={formRef}
      onSubmit={handleSubmit(onFormSubmit)}
      onClose={handleClose}
      headerLeft={
        <Typography variant="h6">
          {t(`payments.campaign.${formMetadata?.isEditing ? 'edit' : 'new'}_content_item`)}
        </Typography>
      }
      headerRight={
        formMetadata?.isEditing && (
          <Button color="error" onClick={handleDeleteClick} data-testid={contentItemFormTestIds.deleteButton}>
            {t('general.delete')}
          </Button>
        )
      }
      footerLeft={
        <Button
          className={classes.footerButton}
          data-testid={contentItemFormTestIds.submitButton}
          onClick={onSaveClick}
        >
          {t('general.save')}
        </Button>
      }
      footerRight={
        <Button color="grey" className={classes.footerButton} onClick={handleClose}>
          {t('general.cancel')}
        </Button>
      }
    >
      <FormProvider {...formMethods}>
        <div className={classes.formBody} data-testid={contentItemFormTestIds.formBody}>
          <div className={classes.formRow}>
            <Controller
              control={control}
              name="type"
              defaultValue={HeroModuleItemType.VOD}
              render={({ field: { value, onChange } }) => (
                <ContentTypeSelector
                  options={contentTypeOptions}
                  value={value}
                  onChange={onChange}
                  callback={(selectedValue: HeroModuleItemType) => {
                    onChange(selectedValue);
                  }}
                />
              )}
            />
          </div>

          <div className={classes.formRow}>
            {itemType === HeroModuleItemType.VOD && (
              <Controller
                control={control}
                name="contentId"
                rules={{ required: t('general.field_is_required') }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div className={classes.contentPicker}>
                    {renderFormLabel(markAsRequired(t('layouts.content')), !!error)}
                    <ContentPicker value={value} onChange={onChange} error={!!error} />
                  </div>
                )}
              />
            )}
          </div>
        </div>
      </FormProvider>
    </Drawer>
  );
}
